import { animationRangeValues, WeatherSettingsChangeSource, WeatherTimelineRange, WeatherTimelineTimeMode } from '@fe/weather';
import { Button } from '@progress/kendo-react-buttons';
import { Slider, SliderChangeEvent, SliderHandle } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import React, { useCallback } from 'react';
import { EventBus, useEvent } from '../../app/contexts/event-bus';
import { useWorkspace } from '../../app/features/home/hooks/use-view';
import { DateTimeFormatter } from '../../formatters';
import { CogOutline, PauseCircleOutline, PlayCircleOutline } from '../../icons/icons';
import useWeatherService from '../hooks/use-weather-service';
import useSelectedWeather from './hooks/use-selected-weather';

interface AnimationWindow {
    min: number;
    max: number;
    spanHours: number;
    timelineMode: Exclude<WeatherTimelineRange, WeatherTimelineRange.auto>;
}

interface Tick {
    percentage: string;
    size: 'xl' | 'lg' | 'md' | 'sm';
    label?: string;
}

export default function WeatherTimeline() {
    const weatherService = useWeatherService();
    const selectedProducts = useSelectedWeather({
        // Ignore this one to avoid too many updates
        ignoreEvents: ['visibleFrameChanged']
    });

    const timelineMode = useWeatherTimeMode();
    const loadedWeather = React.useMemo(
        () => selectedProducts.filter((x) => x.loaded && x.success && x.weatherStore && x.visible),
        [selectedProducts]
    );

    const [timeNow, setTimeNow] = React.useState(0);
    const [selectedTime, setSelectedTime] = React.useState(0);
    const [animationRunning, setAnimationRunning] = React.useState(false);
    const [animationWindow, setAnimationWindow] = React.useState<AnimationWindow>({
        min: 0,
        max: 0,
        spanHours: 1,
        timelineMode: WeatherTimelineRange.historyShort
    });

    const sliderRef = React.useRef<SliderHandle>(null);
    const fastForward = useWorkspace((x) => x.flights.fastForward);

    const ticks = React.useMemo(() => {
        const range = animationRangeValues[animationWindow.timelineMode];
        const spanHours = animationWindow.spanHours;
        const absolute = timelineMode === WeatherTimelineTimeMode.absolute;
        const minTime = animationWindow.min;

        const ticks: Tick[] = [];
        for (let hh = 0; hh < spanHours + 1; hh++) {
            const time = minTime + hh * 3600;
            const relTime = range.min + hh;
            ticks.push({
                size: 'xl',
                percentage: ((hh / spanHours) * 100).toFixed(2) + '%',
                label: absolute ? DateTimeFormatter.formatTime(time, 'hh') : formatRelativeHour(relTime, timeNow)
            });

            // Don't add labels to the right of the last tick
            if (hh === spanHours) {
                break;
            }

            if (range.smallTickMinutes) {
                for (let mm = range.smallTickMinutes; mm < 60; mm += range.smallTickMinutes) {
                    ticks.push({
                        percentage: (((hh + mm / 60) / spanHours) * 100).toFixed(2) + '%',
                        size: 'sm'
                    });
                }
            }

            if (range.mediumTickMinutes) {
                for (let mm = range.mediumTickMinutes; mm < 60; mm += range.mediumTickMinutes) {
                    ticks.push({
                        percentage: (((hh + mm / 60) / spanHours) * 100).toFixed(2) + '%',
                        size: 'md'
                    });
                }
            }

            if (range.bigTickMinutes) {
                for (let mm = range.bigTickMinutes; mm < 60; mm += range.bigTickMinutes) {
                    ticks.push({
                        percentage: (((hh + mm / 60) / spanHours) * 100).toFixed(2) + '%',
                        size: 'lg'
                    });
                }
            }
        }
        return ticks;
    }, [animationWindow, timelineMode, timeNow]);

    const sliderValue = React.useMemo(
        () => (selectedTime - animationWindow.min) / animationWindow.spanHours / 3600,
        [selectedTime, animationWindow]
    );

    React.useEffect(() => {
        if (!weatherService) {
            return;
        }

        setAnimationWindow(weatherService.timeline.getWindow());
        return weatherService.on('timeline.window-changed', () => {
            setAnimationWindow(weatherService.timeline.getWindow());
        }).unsubscribe;
    }, [weatherService]);

    React.useEffect(() => {
        if (!weatherService) {
            return;
        }

        setTimeNow(weatherService.timeline.getTimeNow());
        return weatherService.on('timeline.time-now-changed', () => {
            setTimeNow(weatherService.timeline.getTimeNow());
        }).unsubscribe;
    }, [weatherService]);

    // Converts unix time to percentage on the timeline scale
    const timeToPercent = React.useCallback(
        (time: number) => {
            return (((time - animationWindow.min) / animationWindow.spanHours / 3600) * 100).toFixed(2) + '%';
        },
        [animationWindow]
    );

    React.useEffect(() => {
        if (!weatherService) {
            return;
        }

        setSelectedTime(weatherService.timeline.getSelectedTime());
        return weatherService.on('timeline.selected-time-changed', () => {
            setSelectedTime(weatherService.timeline.getSelectedTime());
        }).unsubscribe;
    }, [weatherService]);

    React.useEffect(() => {
        // Animation running is part of the udview, if the view changes reload the status as well
        setAnimationRunning(weatherService.getSettings().animation.running);
        return weatherService?.on('view.changed', () => {
            setAnimationRunning(weatherService.getSettings().animation.running);
        }).unsubscribe;
    }, [weatherService]);

    // Tooltip sync
    React.useEffect(() => {
        const element: HTMLElement | null = (sliderRef.current as any)?._element;
        const handle = element?.querySelector('.k-draghandle') as HTMLElement | null | undefined;
        if (handle) {
            handle.title = 'Selected: ' + DateTimeFormatter.formatTime(selectedTime, 'hm');
        }
    }, [selectedTime, sliderRef.current]);

    const handleAnimationToggle = () => {
        setAnimationRunning(!animationRunning);
        if (animationRunning) {
            weatherService?.timeline.stopAnimation();
        } else {
            weatherService?.timeline.startAnimation();
        }

        weatherService?.updateSettings((s) => {
            s.animation.running = !animationRunning;
        }, WeatherSettingsChangeSource.animationRunning);
    };

    const handleOpenSettings = React.useCallback(() => {
        EventBus.fire('open-weather-settings', { category: 99 });
    }, []);

    const handleSliderValueChange = React.useCallback(
        (e: SliderChangeEvent) => {
            setAnimationRunning(false);
            const time = Math.floor(e.value * animationWindow.spanHours * 3600 + animationWindow.min);
            weatherService?.timeline.setSelectedTime(time);
            weatherService?.timeline.stopAnimation();
        },
        [weatherService, animationWindow]
    );

    const handleDiamondClicked = React.useCallback(
        (time: number | null) => {
            if (!time) {
                return;
            }
            weatherService?.timeline.setSelectedTime(time);
            weatherService?.timeline.stopAnimation();
        },
        [weatherService]
    );

    useEvent(
        'fast-forward-progress',
        useCallback(
            (e) => {
                if (fastForward.active) {
                    const item = fastForward.points?.[e];
                    if (item && item.time) {
                        weatherService?.timeline.setSelectedTime(item.time);
                        weatherService?.timeline.stopAnimation();
                    }
                }
            },
            [weatherService, fastForward]
        )
    );

    if (!weatherService || loadedWeather.length === 0) {
        return <></>;
    }

    return (
        <Tooltip
            position="top"
            anchorElement="target"
            openDelay={0}
            filter={(x) => x.classList.contains('k-draghandle') || x.classList.contains('timeline-diamond')}>
            <div className="k-card p-2 ps-3 px-2 overflow-hidden map-tools-bg-color" id="weather-time-slider">
                <div className="d-flex gap-3 align-items-center">
                    <div className="flex-grow-1 position-relative" style={{ height: '20px' }}>
                        {/* Main axis */}
                        <div
                            style={{
                                left: 0,
                                right: 0,
                                position: 'absolute'
                            }}>
                            <div style={{ position: 'relative', top: '-13px' }}>
                                <Slider
                                    ref={sliderRef}
                                    min={0}
                                    max={1}
                                    className="w-100 m-0 custom-track no-focus rectangle-draghandle-v"
                                    value={sliderValue}
                                    onChange={handleSliderValueChange}
                                />
                            </div>
                        </div>

                        <Ticks ticks={ticks} />

                        <div
                            style={{
                                position: 'absolute',
                                top: '-8px',
                                width: '8px',
                                height: '8px',
                                left: timeToPercent(timeNow),
                                transform: 'translate(-50%, 0)'
                            }}>
                            <svg viewBox="0 0 11 11" xmlns="http://www.w3.org/2000/svg" width={8} height={8}>
                                <path d="M0,0 l11,0 l-5,10 l-1,0 z" fill="var(--border-color)" />
                            </svg>
                        </div>

                        {/* <div
                        style={{
                            position: 'absolute',
                            fontSize: '0.8em',
                            transform: 'translate(-50%, 0)',
                            left: `${sliderValue * 100}%`,
                            top: '5px'
                        }}>
                        {DateTimeFormatter.formatTime(Math.floor(state.minTime + sliderValue * state.spanSeconds))}
                    </div> */}

                        {/* Available frames */}
                        {loadedWeather
                            .map((x) => x.weatherStore!.frames)
                            .flat(2)
                            .map((frame, i) => (
                                <div
                                    onClick={() => handleDiamondClicked(frame.times.validFrom)}
                                    key={i}
                                    className="d-flex align-items-center justify-content-center cursor-pointer timeline-diamond"
                                    title={DateTimeFormatter.formatTime(frame.times.validFrom)}
                                    style={{
                                        height: '20px',
                                        width: '15px',
                                        position: 'absolute',
                                        top: '0px',
                                        left: timeToPercent(frame.times.validFrom ?? 0),
                                        transform: 'translate(-50%, -50%)'
                                    }}>
                                    <div
                                        className="pe-none"
                                        style={{
                                            height: '6px',
                                            width: '6px',
                                            backgroundColor: 'var(--fe-primary)',
                                            transform: 'rotate(45deg)'
                                        }}></div>
                                </div>
                            ))}
                    </div>

                    <div className="d-flex gap-1">
                        <Button
                            svgIcon={animationRunning ? PauseCircleOutline : PlayCircleOutline}
                            className="button-small"
                            fillMode={'outline'}
                            onClick={handleAnimationToggle}
                        />

                        <Button svgIcon={CogOutline} className="button-small" fillMode={'outline'} onClick={handleOpenSettings} />
                    </div>
                </div>
            </div>
        </Tooltip>
    );
}

function Ticks(props: { ticks: Tick[] }) {
    return (
        <>
            {/* Tics */}
            {props.ticks.map((x, i) => (
                <div
                    key={i}
                    style={{
                        position: 'absolute',
                        height: x.size === 'xl' ? '10px' : x.size === 'lg' ? '8px' : x.size === 'md' ? '6px' : '4px',
                        backgroundColor: 'var(--border-color)',
                        transform: 'translate(-50%, 0)',
                        width: '2px',
                        left: x.percentage
                    }}></div>
            ))}
            {/* Labels */}
            {props.ticks
                .filter((x) => x.label)
                .map((x, i) => (
                    <div
                        key={i}
                        style={{
                            fontSize: '0.7em',
                            position: 'absolute',
                            transform: 'translate(-50%, 0)',
                            left: x.percentage,
                            top: '12px'
                        }}>
                        {x.label}
                    </div>
                ))}
        </>
    );
}

function formatRelativeHour(input: number, now: number): string {
    if (input === 0) {
        return DateTimeFormatter.formatTime(now, 'hmnd');
    }

    return (input > 0 ? '+' : '') + input;
}

function useWeatherTimeMode() {
    const weatherService = useWeatherService();
    const [settings, setSettings] = React.useState<WeatherTimelineTimeMode>(WeatherTimelineTimeMode.absolute);

    React.useEffect(() => {
        if (!weatherService) {
            return;
        }

        setSettings(weatherService?.getSettings().animation.timelineTimeMode);

        return weatherService.on('settings.changed', (e) => {
            if (e.changeSource === WeatherSettingsChangeSource.animation) {
                setSettings(e.settings.animation.timelineTimeMode);
            }
        }).unsubscribe;
    }, [weatherService]);

    return settings;
}
